import { API_ENDPOINTS } from '../../services/apiEndpoints';
import { apiUtils } from '../../hooks/useApi';
import {
  isStandardQuestionnaire,
  isGroupedQuestionnaire,
} from '../../utils/questionnaireUtils';
import { checkForMissingAttachments } from '../../utils/attachmentValidationUtils';
import { getNextStepKey } from '../utils';
import type {
  SaveAnswersRequest,
  SaveAnswersResponse,
  Answer,
  GroupedQuestionnaireData,
  StandardQuestionnaireData,
} from '../../types/api';
import { throwApiError } from '../../utils';

/**
 * Service to save answers to the API
 */
export const saveAnswersService = async (
  hotelId: string,
  questionnaireData: GroupedQuestionnaireData | StandardQuestionnaireData,
  answers: Record<string, Answer>,
  isDraft = false,
  updateQuestionUploadedFilenames?: (
    questionId: string,
    uploadedFilenames: string[]
  ) => void,
  isFasttrack?: boolean
): Promise<SaveAnswersResponse> => {
  console.log('💾 Saving answers:', {
    hotelId,
    stepKey: questionnaireData.stepKey,
    answers,
  });

  // Filter out answers for excluded questions before saving
  // Cleanup happens here during save, not automatically on every answer change
  let cleanedAnswers = { ...answers };

  // Provide a no-op function if updateQuestionUploadedFilenames is not available
  const uploadedFilenamesUpdater =
    updateQuestionUploadedFilenames ||
    ((questionId: string) => {
      console.warn(
        `updateQuestionUploadedFilenames not provided - cannot clear uploaded_filenames for question ${questionId}`
      );
    });

  if (isStandardQuestionnaire(questionnaireData)) {
    // For standard questionnaires, cleanup excluded question answers and their attachments
    const { cleanupExcludedQuestionAnswersWithAttachments } = await import(
      '../../utils/dependencyUtils'
    );
    const { cleanedAnswers: cleaned } =
      await cleanupExcludedQuestionAnswersWithAttachments(
        hotelId,
        answers,
        questionnaireData.metadata.fields,
        uploadedFilenamesUpdater
      );
    cleanedAnswers = cleaned;

    // Clear attachments for questions with invalid attachment options
    try {
      const { clearAttachmentsForInvalidOptions } = await import(
        '../../utils/attachmentDeletionUtils'
      );
      await clearAttachmentsForInvalidOptions(
        hotelId,
        questionnaireData.metadata.fields,
        cleanedAnswers,
        uploadedFilenamesUpdater
      );
    } catch (error) {
      console.error('Failed to clear attachments for invalid options:', error);
      // Continue with save even if attachment clearing fails
    }
  } else if (isGroupedQuestionnaire(questionnaireData)) {
    // For grouped questionnaires, cleanup excluded question answers and their attachments
    const { cleanupExcludedGroupedQuestionAnswersWithAttachments } =
      await import('../../utils/groupedDependencyUtils');
    const allGroupedFields = questionnaireData.groups.flatMap(
      group => group.metadata.fields
    );

    const { cleanedAnswers: cleaned } =
      await cleanupExcludedGroupedQuestionAnswersWithAttachments(
        hotelId,
        answers,
        allGroupedFields,
        uploadedFilenamesUpdater
      );
    cleanedAnswers = cleaned;

    // Clear attachments for questions with invalid attachment options
    try {
      const { clearAttachmentsForInvalidOptions } = await import(
        '../../utils/attachmentDeletionUtils'
      );
      await clearAttachmentsForInvalidOptions(
        hotelId,
        allGroupedFields,
        cleanedAnswers,
        uploadedFilenamesUpdater
      );
    } catch (error) {
      console.error('Failed to clear attachments for invalid options:', error);
    }
  }

  console.log('🧹 Cleaned answers before saving:', {
    originalCount: Object.keys(answers).length,
    cleanedCount: Object.keys(cleanedAnswers).length,
    removedAnswers: Object.keys(answers).filter(key => !cleanedAnswers[key]),
    cleanedAnswers,
  });

  const stepKey = questionnaireData.stepKey;

  const request: SaveAnswersRequest = {
    hotelId,
    stepKey,
    answers: cleanedAnswers, // Use cleaned answers instead of original
    saveAsDraft: isDraft,
    metadata: {
      completedAt: new Date().toISOString(),
    },
  };

  console.log('📤 Request payload:', request);

  // Check for missing attachments before saving
  const hasMissingAttachments = checkForMissingAttachments(
    questionnaireData,
    cleanedAnswers
  );
  // const hasMissingAttachments = null;

  // console.log('📎 Missing attachments check:', hasMissingAttachments);

  const response = await apiUtils.post(
    API_ENDPOINTS.hotels.saveAnswers(hotelId, stepKey, isFasttrack),
    request
  );

  console.log('response in answers service after direct api call ::', response);

  const constructedResponse: SaveAnswersResponse = {
    success: response.data.success,
    message: 'Answers saved successfully',
    stepCompleted: true,
    nextStepKey: getNextStepKey(stepKey),
    hasMissingAttachments: hasMissingAttachments || false, // Include the missing attachments flag
    refetch : response.data?.data?.refetch ?? false, // Include refetch flag from API response if available
  };

  console.log('✅ Save Mock Response:', constructedResponse );

  return constructedResponse;
};

/**
 * service to save hotel details section answers
 */
export const saveHotelDetailsAnswerService = async (
  hotelId: string,
  answers: Record<string, any>
): Promise<SaveAnswersResponse> => {
  try {
    const simplifiedAnswers = Object.values(answers).reduce(
      (acc, ans) => ({
        ...acc,
        [ans.questionId]:
          ans.value.selectedOptionId ?? ans.value.typedValue ?? null,
      }),
      {}
    );

    console.log('💾 Saving hotel details answers:', simplifiedAnswers);
    const response = await apiUtils.put(
      API_ENDPOINTS.hotels.updateHotelDetails(hotelId),
      {
        ...simplifiedAnswers,
      }
    );
    console.log('✅ Hotel details saved successfully:', response.data);
    
    // Return SaveAnswersResponse structure to match saveAnswersService
    const saveResponse: SaveAnswersResponse = {
      success: true,
      message: 'Hotel details saved successfully',
      stepCompleted: true,
      nextStepKey: getNextStepKey('hotel-location'),
      hasMissingAttachments: false,
    };
    
    return saveResponse;
  } catch (error) {
    console.log('Failed to save hotel details answers:', error);
    throw error;
    // throwApiError(error, 'Failed to save hotel details answers');
  }
};
